import { motion } from "framer-motion";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";

export interface StreamLocationDatum {
  id: number;
  value: number;
  label: string;
  color: string;
}

interface StreamLocationsCardProps {
  totalViews: number;
  data: StreamLocationDatum[];
}

export default function StreamLocationsCard({
  totalViews,
  data,
}: StreamLocationsCardProps) {
  const isEmpty = data.length === 0 || totalViews === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
    >
      <Card sx={{ borderRadius: 3, height: "100%", boxShadow: 0 }}>
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: "100%",
          }}
        >
          {isEmpty ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Stream Locations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No location report
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Stream Locations
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {totalViews.toLocaleString("en-US").replace(/,/g, " ")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Total Views
                </Typography>

                <Box>
                  {data.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color,
                          borderRadius: 0.5,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {item.value}%
                        </Box>{" "}
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  width: 200,
                  height: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PieChart
                  series={[
                    {
                      data,
                      paddingAngle: 5,
                      innerRadius: "62%",
                      outerRadius: "100%",
                      cornerRadius: 25,
                      startAngle: -120,
                      endAngle: 240,
                    },
                  ]}
                  width={200}
                  height={200}
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  hideLegend
                  colors={data.map((item) => item.color)}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
